// AUD-4 — MobileBrief: the daily AUDIO BRIEF player (Phase 2, mobile surface).
//
// READ-ONLY and offline-aware. The episode read + playback are owned by `useBriefAudio` (built on the
// shipped offline substrate): last-known-good is cached, a synchronous placeholder paints before the
// network round-trip, and the page never blanks offline.
//
// The `research_brief_episodes` table is EMPTY until research-tts-prebrief runs (gated), so a null
// episode is a valid, non-error state → a calm empty card, never an error banner. If an episode exists
// but its audio isn't available offline while offline, we show a quiet "not downloaded for offline"
// note rather than an error.
//
// Conventions mirror MobilePortfolioGlance / MobileMeetingPrep exactly: useIsOnline header with a
// "Saved <relative time>" stale hint (date-fns formatDistanceToNow), cn/tailwind, lucide-react icons.

import { useMemo } from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';
import { ChevronLeft, Pause, Play, Headphones, WifiOff } from 'lucide-react';
import { useIsOnline } from '@/hooks/useIsOnline';
import { useBriefAudio } from '@/hooks/useBriefAudio';
import { cn } from '@/lib/utils';

// Seconds → m:ss (or h:mm:ss for long briefs).
function fmtTime(total: number): string {
  if (!Number.isFinite(total) || total < 0) total = 0;
  const s = Math.floor(total % 60);
  const m = Math.floor((total / 60) % 60);
  const h = Math.floor(total / 3600);
  const ss = String(s).padStart(2, '0');
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${ss}`;
  return `${m}:${ss}`;
}

function prettySurface(surface: string | null): string {
  if (!surface) return 'Daily brief';
  return surface
    .split(/[_\-\s]+/)
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');
}

// Short label for the surface selector chips. CIO/PM are acronyms (kept upper-cased); everything else
// falls back to the title-cased prettySurface form (Comprehensive, Analyst, …).
function surfaceChipLabel(surface: string | null): string {
  const key = (surface ?? '').toLowerCase();
  if (key === 'cio') return 'CIO';
  if (key === 'pm') return 'PM';
  if (key === 'comprehensive') return 'Comprehensive';
  if (key === 'analyst') return 'Analyst';
  return prettySurface(surface);
}

export default function MobileBrief() {
  const online = useIsOnline();
  const brief = useBriefAudio();

  const {
    episodes,
    selectedSurface,
    setSelectedSurface,
    episode,
    chapters,
    playing,
    currentTime,
    duration,
    rate,
    isFromCache,
    cachedAt,
    loading,
    downloaded,
    error,
    toggle,
    seek,
    setRate,
    skipToChapter,
  } = brief;

  // Stale/offline affordance (mirrors the sibling mobile pages' header line).
  const showStale = isFromCache && cachedAt != null;
  let staleLabel: string | null = null;
  if (showStale && cachedAt) {
    try {
      staleLabel = `Saved ${formatDistanceToNow(new Date(cachedAt), { addSuffix: true })}`;
    } catch {
      staleLabel = 'Showing saved data';
    }
  }

  const dateLabel = useMemo(() => {
    if (!episode?.target_date) return null;
    try {
      return format(new Date(episode.target_date), 'EEE d MMM yyyy');
    } catch {
      return episode.target_date;
    }
  }, [episode?.target_date]);

  // Current chapter index for highlighting + progress context.
  const currentChapterIdx = useMemo(() => {
    let idx = -1;
    for (let i = 0; i < chapters.length; i++) {
      if (chapters[i].start_sec <= currentTime) idx = i;
      else break;
    }
    return idx;
  }, [chapters, currentTime]);

  const pct = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;

  // The episode exists but we can't actually play it offline: no signed URL resolved AND no on-disk
  // download was found (the hook probes the Filesystem when offline, so `downloaded` flips true if a
  // brief was saved on a previous online run). Surface a gentle note, not an error.
  const audioUnavailableOffline =
    !!episode && !!episode.audio_path && !online && !downloaded && !brief.signedUrl;

  return (
    <div className="min-h-screen bg-background px-4 pb-8 pt-[calc(1.25rem+env(safe-area-inset-top))]">
      <header className="mb-4">
        <Link
          to="/partner/m/home"
          className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          Home
        </Link>
        <p className="text-sm text-muted-foreground">Audio brief</p>
        <h1 className="font-heading text-2xl font-semibold text-primary">
          {dateLabel ?? 'Daily brief'}
        </h1>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
          {episode?.surface && (
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground/70">
              {prettySurface(episode.surface)}
            </p>
          )}
          {showStale && staleLabel && (
            <p
              className={cn(
                'text-[11px] font-medium uppercase tracking-wide',
                online ? 'text-muted-foreground/70' : 'text-amber-600',
              )}
            >
              {staleLabel}
            </p>
          )}
        </div>
      </header>

      {!episode ? (
        // Empty state — the table is empty until research-tts-prebrief runs. Calm, never an error.
        <section className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Headphones className="h-5 w-5" aria-hidden="true" />
            </span>
            <h2 className="font-heading text-sm font-semibold">No brief yet</h2>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            {loading
              ? 'Loading your brief…'
              : 'No brief yet — your daily audio brief will appear here once generated.'}
          </p>
          {!online && !loading && (
            <p className="mt-2 text-[11px] uppercase tracking-wide text-amber-600">
              You’re offline — this will refresh once you reconnect.
            </p>
          )}
        </section>
      ) : (
        <div className="space-y-3">
          {/* Surface selector — only when the user has more than one episode for this date. Tapping a
              chip loads + plays that surface's episode through the same player below. */}
          {episodes.length > 1 && (
            <div
              role="tablist"
              aria-label="Choose brief"
              className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              {episodes.map((ep) => {
                const key = (ep.surface ?? '').toLowerCase();
                const active = key === selectedSurface;
                return (
                  <button
                    key={ep.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setSelectedSurface(key)}
                    className={cn(
                      'shrink-0 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors',
                      active
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-card text-muted-foreground active:bg-muted/50',
                    )}
                  >
                    {surfaceChipLabel(ep.surface)}
                  </button>
                );
              })}
            </div>
          )}

          {/* Player card */}
          <section className="rounded-xl border border-border bg-card p-4">
            {/* Transport */}
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={toggle}
                aria-label={playing ? 'Pause brief' : 'Play brief'}
                aria-pressed={playing}
                className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow active:opacity-90"
              >
                {playing ? (
                  <Pause className="h-6 w-6" aria-hidden="true" />
                ) : (
                  <Play className="ml-0.5 h-6 w-6" aria-hidden="true" />
                )}
              </button>

              <div className="min-w-0 flex-1">
                {/* Seekable progress bar */}
                <input
                  type="range"
                  min={0}
                  max={Math.max(1, Math.floor(duration))}
                  step={1}
                  value={Math.min(currentTime, duration || currentTime)}
                  onChange={(e) => seek(Number(e.target.value))}
                  aria-label="Seek within the brief"
                  className="h-2 w-full cursor-pointer appearance-none rounded-full bg-muted accent-primary"
                  style={{
                    background: `linear-gradient(to right, hsl(var(--primary)) ${pct}%, hsl(var(--muted)) ${pct}%)`,
                  }}
                />
                <div className="mt-1 flex items-center justify-between font-mono text-[11px] tabular-nums text-muted-foreground">
                  <span>{fmtTime(currentTime)}</span>
                  <span>{fmtTime(duration)}</span>
                </div>
              </div>

              {/* 1x / 1.5x rate toggle */}
              <button
                type="button"
                onClick={() => setRate(rate === 1 ? 1.5 : 1)}
                aria-label={`Playback speed ${rate === 1 ? '1x' : '1.5x'}, tap to change`}
                className="shrink-0 rounded-full border border-border px-2.5 py-1 text-xs font-semibold tabular-nums text-muted-foreground active:bg-muted/50"
              >
                {rate === 1 ? '1×' : '1.5×'}
              </button>
            </div>

            {audioUnavailableOffline && (
              <p className="mt-3 flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-amber-600">
                <WifiOff className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                Not downloaded for offline — reconnect to listen.
              </p>
            )}
            {error && !audioUnavailableOffline && (
              <p className="mt-3 text-[11px] uppercase tracking-wide text-amber-600">{error}</p>
            )}
            {downloaded && (
              <p className="mt-3 text-[11px] uppercase tracking-wide text-muted-foreground/70">
                Saved for offline listening
              </p>
            )}
          </section>

          {/* Chapters */}
          {chapters.length > 0 && (
            <section className="rounded-xl border border-border bg-card p-4">
              <h2 className="font-heading text-sm font-semibold">Chapters</h2>
              <ul className="mt-2 space-y-1">
                {chapters.map((c, i) => {
                  const active = i === currentChapterIdx;
                  return (
                    <li key={`${c.start_sec}-${i}`}>
                      <button
                        type="button"
                        onClick={() => skipToChapter(i)}
                        aria-current={active ? 'true' : undefined}
                        className={cn(
                          'flex w-full items-start justify-between gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors',
                          active ? 'bg-primary/10 text-primary' : 'hover:bg-muted/50',
                        )}
                      >
                        <span className="min-w-0 flex-1">
                          <span className="block">
                            <span className="mr-2 text-muted-foreground/60 tabular-nums">{i + 1}.</span>
                            {c.title}
                          </span>
                          {/* Forward-compatible: a later edge-function change adds chapters[].summary.
                              Render it when present, else show the title alone. */}
                          {c.summary ? (
                            <span
                              className={cn(
                                'mt-0.5 block text-[13px] leading-snug',
                                active ? 'text-primary/80' : 'text-muted-foreground',
                              )}
                            >
                              {c.summary}
                            </span>
                          ) : null}
                        </span>
                        <span className="mt-0.5 shrink-0 font-mono text-[11px] tabular-nums text-muted-foreground">
                          {fmtTime(c.start_sec)}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}
        </div>
      )}

      {/* Hidden audio element is owned by the hook (created via `new Audio()`); nothing to render here.
          We keep a no-op anchor comment so the player's element is clearly hook-driven. */}
    </div>
  );
}
